import { Box, TextField } from '@mui/material';
import { ClientGender, ClientIDNumberTypes, NaturalPerson } from '../../../../common/types/client';
import { Controller } from 'react-hook-form';
import { EditClientStepProps } from '../../../types';
import { JSX } from 'react';
import { MARRIED_MARITAL_STATUS_ID } from '../../../constants';
import {
  validateAfricanId,
  validateNamibianId,
  validatePassportId,
  validationRegExpPatterns,
  validationsErrorMessages,
} from '../../../../common/lib/validations';
import AsyncFormSelect from '../../../../common/components/form/asyncFormSelect';
import DateTextField from '../../../../common/components/form/dateTextField';

/**
 * Natural person basic information edit form
 */
const BasicInformationStep = ({
  control,
  watch,
}: EditClientStepProps<NaturalPerson>): JSX.Element => {
  const watchIDNumberTypeID = watch('idNumberTypeID', 0);
  const watchMaritalStatusID = watch('maritalStatusID');
  const birthDate = watch('dateOfBirth');
  const genderID = watch('genderID');

  return (
    <Box className="grid gap-8 md:grid-cols-3">
      <Controller
        name="firstname"
        control={control}
        rules={{
          required: { value: true, message: validationsErrorMessages.required },
          maxLength: { value: 20, message: validationsErrorMessages.maxLength },
          pattern: {
            value: validationRegExpPatterns.namePattern,
            message: validationsErrorMessages.name,
          },
        }}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <TextField
            {...field}
            label="First name(s) as per ID document *"
            placeholder="e.g. John"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        name="middlename"
        control={control}
        rules={{
          maxLength: { value: 20, message: validationsErrorMessages.maxLength },
          pattern: {
            value: validationRegExpPatterns.namePattern,
            message: validationsErrorMessages.name,
          },
        }}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <TextField
            {...field}
            label="Second name(s) as per ID document"
            placeholder="e.g. John"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        name="surname"
        control={control}
        rules={{
          required: { value: true, message: validationsErrorMessages.required },
          maxLength: { value: 50, message: validationsErrorMessages.maxLength },
          pattern: {
            value: validationRegExpPatterns.namePattern,
            message: validationsErrorMessages.name,
          },
        }}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <TextField
            {...field}
            label="Last name as per ID document *"
            placeholder="e.g. Doe"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        name="taxStatusID"
        control={control}
        rules={{
          required: { value: true, message: validationsErrorMessages.required },
          min: { value: 1, message: validationsErrorMessages.required },
        }}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <AsyncFormSelect
            {...field}
            label="Tax status *"
            error={Boolean(error)}
            helperText={error?.message}
            optionsEndpoint="/TaxStatuss?TypeID=1"
          />
        )}
      />
      <Controller
        name="dateOfBirth"
        control={control}
        rules={{
          required: { value: true, message: validationsErrorMessages.required },
          pattern: {
            value: validationRegExpPatterns.datePattern,
            message: validationsErrorMessages.date,
          },
        }}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <DateTextField
            {...field}
            label="Date of birth *"
            placeholder="e.g. 1972/11/28"
            error={!!error}
            helperText={error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="genderID"
        rules={{
          required: { value: true, message: validationsErrorMessages.required },
          min: { value: 1, message: validationsErrorMessages.required },
        }}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <AsyncFormSelect
            {...field}
            label="Gender *"
            error={Boolean(error)}
            helperText={error?.message}
            optionsEndpoint="/Genders"
          />
        )}
      />
      <Box className="flex space-x-2">
        <Controller
          key={watchIDNumberTypeID}
          name={watchIDNumberTypeID === ClientIDNumberTypes.passport ? 'passport' : 'idNumber'}
          control={control}
          rules={{
            required: { value: true, message: validationsErrorMessages.required },
            validate: (value: string): boolean | string => {
              switch (watchIDNumberTypeID) {
                case ClientIDNumberTypes.southAfrica: {
                  return (
                    validateAfricanId(
                      value,
                      genderID === ClientGender.male ? 'male' : 'female',
                      birthDate,
                    ) || 'Invalid SA ID number'
                  );
                }
                case ClientIDNumberTypes.passport: {
                  return validatePassportId(value) || 'Invalid passport number';
                }
                case ClientIDNumberTypes.namibia: {
                  return validateNamibianId(value) || 'Invalid  Namibian ID number';
                }
                default: {
                  return true;
                }
              }
            },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <TextField
              {...field}
              fullWidth
              label="Identification number *"
              placeholder="e.g. 123456789"
              error={!!error}
              helperText={error?.message}
            />
          )}
        />
        <Controller
          name="idNumberTypeID"
          control={control}
          rules={{
            required: { value: true, message: validationsErrorMessages.required },
            min: { value: 1, message: validationsErrorMessages.required },
          }}
          render={({ field, fieldState: { error } }): JSX.Element => (
            <AsyncFormSelect
              {...field}
              label="Identification number type *"
              error={Boolean(error)}
              helperText={error?.message}
              optionsEndpoint="/PersonDetailIDNumberTypes"
            />
          )}
        />
      </Box>
      <Controller
        name="maritalStatusID"
        control={control}
        rules={{
          required: { value: true, message: validationsErrorMessages.required },
          min: { value: 1, message: validationsErrorMessages.required },
        }}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <AsyncFormSelect
            {...field}
            label="Marital status *"
            error={Boolean(error)}
            helperText={error?.message}
            optionsEndpoint="/MaritalStatuss"
          />
        )}
      />
      <Controller
        name="titleID"
        control={control}
        rules={{
          required: { value: true, message: validationsErrorMessages.required },
          min: { value: 1, message: validationsErrorMessages.required },
        }}
        render={({ field, fieldState: { error } }): JSX.Element => (
          <AsyncFormSelect
            {...field}
            label="Title *"
            helperText={error?.message}
            error={Boolean(error)}
            optionsEndpoint={`/Titles?GenderIDList=0,${genderID}`}
          />
        )}
      />

      {watchMaritalStatusID === MARRIED_MARITAL_STATUS_ID && (
        <>
          {genderID === ClientGender.female && (
            <Controller
              name="previousSurname"
              control={control}
              rules={{
                maxLength: { value: 50, message: validationsErrorMessages.maxLength },
                pattern: {
                  value: validationRegExpPatterns.namePattern,
                  message: validationsErrorMessages.name,
                },
              }}
              render={({ field, fieldState: { error } }): JSX.Element => (
                <TextField
                  {...field}
                  label="Previous surname"
                  placeholder="e.g. Doe"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
          <Controller
            name="spouseFirstname"
            control={control}
            rules={{
              maxLength: { value: 20, message: validationsErrorMessages.maxLength },
              pattern: {
                value: validationRegExpPatterns.namePattern,
                message: validationsErrorMessages.name,
              },
            }}
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                label="Spouse first name"
                placeholder="e.g. John"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name="spouseSurname"
            control={control}
            rules={{
              maxLength: { value: 50, message: validationsErrorMessages.maxLength },
              pattern: {
                value: validationRegExpPatterns.namePattern,
                message: validationsErrorMessages.name,
              },
            }}
            render={({ field, fieldState: { error } }): JSX.Element => (
              <TextField
                {...field}
                label="Spouse last name"
                placeholder="e.g. John"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          {genderID === ClientGender.male && (
            <Controller
              name="spousePreviousSurname"
              control={control}
              rules={{
                maxLength: { value: 50, message: validationsErrorMessages.maxLength },
                pattern: {
                  value: validationRegExpPatterns.namePattern,
                  message: validationsErrorMessages.name,
                },
              }}
              render={({ field, fieldState: { error } }): JSX.Element => (
                <TextField
                  {...field}
                  label="Spouse previous name"
                  placeholder="e.g. Doe"
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default BasicInformationStep;
